import React, {useEffect, useRef} from 'react';
import {v4 as uuid} from 'uuid';
import {useShallow} from 'zustand/react/shallow';
import {Stack} from '@mui/material';
import {Modal} from '@lightricks/react-design-system';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useViewDismissedEvent from '@contentlab/hooks/analytics/use-view-dismissed-event';
import useViewPresentedEvent from '@contentlab/hooks/analytics/use-view-presented-event';
import useContactSales from '@contentlab/hooks/use-contact-sales';
import freeTrialEndModalStore, {
  freeTrialEndModalActions,
} from '@contentlab/stores/freeTrialEndModalStore';
import {CONTENT_LAB_VIEW_NAMES} from '@/lib/analytics/analyticsConstants';
import FreeTrialEndModalContent from './FreeTrialEndModalContent';

function FreeTrialEndModal() {
  const {
    isFreeTrialEndModalOpen,
    freeTrialEndModalReason,
    freeTrialEndModalSource,
  } = freeTrialEndModalStore(
    useShallow((state) => ({
      isFreeTrialEndModalOpen: state.isFreeTrialEndModalOpen,
      freeTrialEndModalReason: state.freeTrialEndModalReason,
      freeTrialEndModalSource: state.freeTrialEndModalSource,
    }))
  );

  const {viewPresented} = useViewPresentedEvent();
  const {viewDismissed} = useViewDismissedEvent();
  const {uiInteraction} = useUiInteractionEvent();
  const presentationIdRef = useRef<string>('');
  const onContactSalesClick = useContactSales({
    flowName: 'free-trial-end-contact-sales',
    interactionLocation: CONTENT_LAB_VIEW_NAMES.CREDIT_END_MODAL,
  });

  const dismissModal = (reason: string, itemName: string) => {
    const interactionId = uuid();
    uiInteraction({
      item_name: itemName,
      interaction_location: CONTENT_LAB_VIEW_NAMES.CREDIT_END_MODAL,
      interaction_id: interactionId,
    });
    viewDismissed({
      presentation_id: presentationIdRef.current,
      interaction_id: interactionId,
      reason,
    });
    freeTrialEndModalActions.closeAndResetFreeTrialEndModal();
  };

  const handleClose = (reason: string) => {
    dismissModal(reason, 'close_free_trial_end_modal');
  };

  const handleContactSales = () => {
    dismissModal(
      'contact_sales_button_pressed',
      'contact_sales_button_pressed'
    );
    onContactSalesClick();
  };

  useEffect(() => {
    if (isFreeTrialEndModalOpen) {
      const presentationId = uuid();
      presentationIdRef.current = presentationId;
      viewPresented({
        presentation_id: presentationId,
        view_name: CONTENT_LAB_VIEW_NAMES.CREDIT_END_MODAL,
        view_type: 'popup',
        view_location: 'editor_generate',
        source: freeTrialEndModalSource,
      });
    }
  }, [isFreeTrialEndModalOpen, freeTrialEndModalSource, viewPresented]);

  return (
    <Modal
      open={isFreeTrialEndModalOpen}
      testID="free-trial-end-modal"
      width={444}
      handleClose={() => handleClose('modal_closed')}
      fullScreen={false}
    >
      <Stack>
        <FreeTrialEndModalContent
          onBackToEditor={() => handleClose('back_to_editor_button_pressed')}
          onStartProSubscription={handleContactSales}
          reason={freeTrialEndModalReason}
        />
      </Stack>
    </Modal>
  );
}

export default FreeTrialEndModal;
